import React, { useCallback, useEffect, useState } from 'react';
import {
  createOnboardingInvite,
  getOnboardingInvites,
  regenerateOnboardingInvite,
  revokeOnboardingInvite
} from '../../adminApi';
import um from '../../styles/UserManagement.module.css';
import styles from './OnboardUserPanel.module.css';

const ROLE_OPTIONS = [
  { value: 'standard', label: 'standard (default)' },
  { value: 'commissioner', label: 'commissioner' },
  { value: 'admin', label: 'admin' }
];

const OnboardUserPanel = () => {
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [createdUrl, setCreatedUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    role: 'standard',
    phone: '',
    discord_username: '',
    notes: ''
  });

  const load = useCallback(async () => {
    try {
      const { data } = await getOnboardingInvites();
      setInvites(data || []);
      setError('');
    } catch (err) {
      setError('Failed to load invites');
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const activeInvites = invites.filter((invite) => invite.status === 'pending');
  const otherInvites = invites.filter((invite) => invite.status !== 'pending');

  const copyUrl = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      setError('Could not copy link');
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await createOnboardingInvite({
        role: form.role,
        phone: form.phone || undefined,
        discord_username: form.discord_username || undefined,
        notes: form.notes || undefined
      });
      setCreatedUrl(data.url);
      await copyUrl(data.url, data.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create invite');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async (id) => {
    setBusy(true);
    try {
      const { data } = await regenerateOnboardingInvite(id);
      setCreatedUrl(data.url);
      await copyUrl(data.url, data.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to regenerate invite');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id) => {
    setBusy(true);
    try {
      await revokeOnboardingInvite(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke invite');
    } finally {
      setBusy(false);
    }
  };

  const inviteActions = (invite) => {
    if (invite.status === 'pending') {
      return (
        <>
          {invite.url && (
            <button
              type="button"
              className={um.button}
              disabled={busy}
              onClick={() => copyUrl(invite.url, invite.id)}
            >
              {copiedId === invite.id ? 'Copied' : 'Copy link'}
            </button>
          )}
          <button type="button" className={um.button} disabled={busy} onClick={() => handleRegenerate(invite.id)}>
            {copiedId === invite.id && !invite.url ? 'Copied' : 'New link'}
          </button>
          <button type="button" className={um.button} disabled={busy} onClick={() => handleRevoke(invite.id)}>
            Revoke
          </button>
        </>
      );
    }
    if (invite.status === 'expired') {
      return (
        <button type="button" className={um.button} disabled={busy} onClick={() => handleRegenerate(invite.id)}>
          Regenerate
        </button>
      );
    }
    return null;
  };

  const renderInviteMeta = (invite) => (
    <>
      <div className={styles.inviteCardRow}>
        <dt>Status</dt>
        <dd>{invite.status}</dd>
      </div>
      <div className={styles.inviteCardRow}>
        <dt>Role</dt>
        <dd>{invite.role}</dd>
      </div>
      <div className={styles.inviteCardRow}>
        <dt>If accepted now</dt>
        <dd>{invite.join_preview?.label || '—'}</dd>
      </div>
      <div className={styles.inviteCardRow}>
        <dt>Expires</dt>
        <dd>{invite.expires_at ? new Date(invite.expires_at).toLocaleString() : '—'}</dd>
      </div>
      <div className={styles.inviteCardRow}>
        <dt>Created by</dt>
        <dd>{invite.created_by_username || '—'}</dd>
      </div>
      {invite.url && (
        <div className={styles.inviteCardRow}>
          <dt>Link</dt>
          <dd className={styles.inviteUrl}>{invite.url}</dd>
        </div>
      )}
    </>
  );

  const renderInviteList = (list, emptyLabel) => {
    if (list.length === 0) {
      return <p className={styles.hint}>{emptyLabel}</p>;
    }

    return (
      <>
        <div className={styles.inviteTableWrap}>
          <table className={um.table}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Role</th>
                <th>If accepted now</th>
                <th>Expires</th>
                <th>Created by</th>
                <th>Link</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((invite) => (
                <tr key={invite.id}>
                  <td>{invite.status}</td>
                  <td>{invite.role}</td>
                  <td>{invite.join_preview?.label || '—'}</td>
                  <td>{invite.expires_at ? new Date(invite.expires_at).toLocaleString() : '—'}</td>
                  <td>{invite.created_by_username || '—'}</td>
                  <td className={styles.inviteUrlCell}>
                    {invite.url || '—'}
                  </td>
                  <td>{inviteActions(invite)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.inviteCards}>
          {list.map((invite) => (
            <article key={invite.id} className={styles.inviteCard}>
              <dl>{renderInviteMeta(invite)}</dl>
              <div className={styles.inviteCardActions}>{inviteActions(invite)}</div>
            </article>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={`${um.section} ${styles.panel}`}>
      <div className={styles.header}>
        <h4 className={`${um.sectionTitle} ${styles.title}`}>Onboard a new user</h4>
        <button type="button" className={um.button} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Onboard a new user'}
        </button>
      </div>

      {open && (
        <>
          {error && <p className={um.error}>{error}</p>}

          <section className={styles.section}>
            <h5 className={styles.sectionTitle}>Active invites</h5>
            <p className={styles.hint}>
              Pending links stay available here so you can copy them again. Revoke or wait for expiry (72 hours) to retire one.
            </p>
            {renderInviteList(activeInvites, 'No active invites yet.')}
          </section>

          {otherInvites.length > 0 && (
            <section className={styles.section}>
              <h5 className={styles.sectionTitle}>Past invites</h5>
              {renderInviteList(otherInvites, '')}
            </section>
          )}

          <div className={styles.createToggle}>
            <button
              type="button"
              className={um.button}
              onClick={() => setShowCreate((v) => !v)}
            >
              {showCreate ? 'Hide create form' : 'Create new invite'}
            </button>
          </div>

          {showCreate && (
            <form onSubmit={handleCreate} className={styles.form}>
              <div className={um.field}>
                <label className={um.fieldLabel} htmlFor="onboard-role">Role</label>
                <select
                  id="onboard-role"
                  className={um.select}
                  value={form.role}
                  onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className={um.field}>
                <label className={um.fieldLabel} htmlFor="onboard-phone">Phone (optional, for your records / later SMS)</label>
                <input
                  id="onboard-phone"
                  className={styles.input}
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className={um.field}>
                <label className={um.fieldLabel} htmlFor="onboard-discord">Discord username (optional)</label>
                <input
                  id="onboard-discord"
                  className={styles.input}
                  value={form.discord_username}
                  onChange={(e) => setForm((p) => ({ ...p, discord_username: e.target.value }))}
                />
              </div>
              <div className={um.field}>
                <label className={um.fieldLabel} htmlFor="onboard-notes">Admin notes (optional)</label>
                <input
                  id="onboard-notes"
                  className={styles.input}
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
              <button type="submit" className={um.button} disabled={busy}>
                Create invite link
              </button>
              <p className={styles.hint}>
                Treatment (normal / week-1 bye / late join) is applied when they create the account, not when you generate the link.
              </p>
            </form>
          )}

          {createdUrl && (
            <div className={`${um.field} ${styles.createdLink}`}>
              <label className={um.fieldLabel}>Latest link</label>
              <input className={styles.input} readOnly value={createdUrl} />
              <button
                type="button"
                className={um.button}
                onClick={() => copyUrl(createdUrl, 'latest')}
              >
                {copiedId === 'latest' ? 'Copied' : 'Copy link'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OnboardUserPanel;
